import { useEffect, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import { chartGridColor, chartTextColor, chartTooltipTheme } from '../../utils/themeTokens';

Chart.register(...registerables);
Chart.defaults.animation = false;
Chart.defaults.responsive = true;
Chart.defaults.maintainAspectRatio = false;
Chart.defaults.devicePixelRatio = Math.max(2, typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);

const tc = () => chartTextColor();
const gc = () => chartGridColor();
const ttBase = () => chartTooltipTheme();

const FC = { S3:'#ef5350',S2:'#ff7043',S1:'#ff8a65',0:'#78909c','0.382':'#4dd0e1','0.5':'#26c6da','0.618':'#00bcd4',1:'#ffd54f',R1:'#81c784',R2:'#66bb6a',R3:'#4caf50' };

export default function OCCharts({ series, symbol, ohlcDebug }) {
  const ociRef = useRef(null);
  const mpRef = useRef(null);
  const ohlcRef = useRef(null);
  const decRef = useRef(null);
  const fibCtrlRef = useRef(null);
  const ohlcInfoRef = useRef(null);
  const chartsRef = useRef([]);
  const ohlcChartRef = useRef(null);
  const [fibOn, setFibOn] = useState({});

  useEffect(() => {
    chartsRef.current.forEach(c => { try { c.destroy(); } catch (_) {} });
    chartsRef.current = [];
    ohlcChartRef.current = null;

    if (!series) return;

    const { oci, mp, dec, oh, fib } = series;

    if (ociRef.current && oci?.length) {
      const v = oci.map(x => x.oci);
      const am = Math.max(Math.abs(Math.min(...v, 0)), Math.abs(Math.max(...v, 0)));
      const sm = Math.ceil(am / 10000) * 10000 + 10000;
      chartsRef.current.push(new Chart(ociRef.current, {
        type: 'bar',
        data: {
          labels: oci.map(x => x.time),
          datasets: [{ label: 'OCI', data: v, backgroundColor: v.map(x => x >= 0 ? 'rgba(38,166,154,0.82)' : 'rgba(239,83,80,0.82)'), borderWidth: 0 }],
        },
        options: {
          plugins: { legend: { display: false }, tooltip: ttBase() },
          scales: {
            x: { ticks: { color: tc(), font: { size: 9 }, maxRotation: 45 }, grid: { color: gc(), lineWidth: 1, tickLength: 0 } },
            y: { min: -sm, max: sm, ticks: { color: tc(), font: { size: 10 } }, grid: { color: gc(), lineWidth: 1, tickLength: 0 } },
          },
        },
      }));
    }

    if (mpRef.current && mp?.length) {
      const sv = mp.map(x => x.spot), mv = mp.map(x => x.maxpain), pv = mp.map(x => x.pcr);
      const ap = [...sv, ...mv], pMn = Math.min(...ap), pMx = Math.max(...ap), pR = pMx - pMn;
      const pcMn = Math.min(...pv), pcMx = Math.max(...pv), pcR = pcMx - pcMn;
      chartsRef.current.push(new Chart(mpRef.current, {
        type: 'line',
        data: {
          labels: mp.map(x => x.time),
          datasets: [
            { label: 'Spot', data: sv, borderColor: '#D1D4DC', borderWidth: 1.6, tension: 0.25, pointRadius: 0, yAxisID: 'y' },
            { label: 'MaxPain', data: mv, borderColor: '#FF9800', borderWidth: 1.6, borderDash: [4, 3], tension: 0.25, pointRadius: 0, yAxisID: 'y' },
            { label: 'PCR', data: pv, borderColor: '#26a69a', borderWidth: 1.6, tension: 0.25, pointRadius: 0, yAxisID: 'y1' },
          ],
        },
        options: {
          interaction: { mode: 'index', intersect: false },
          plugins: { legend: { display: false }, tooltip: ttBase() },
          scales: {
            x: { ticks: { color: tc(), font: { size: 9 }, maxRotation: 45 }, grid: { color: gc(), lineWidth: 1, tickLength: 0 } },
            y: { min: Math.floor(pMn - pR * 0.05), max: Math.ceil(pMx + pR * 0.05), ticks: { color: tc(), font: { size: 10 } }, grid: { color: gc(), lineWidth: 1, tickLength: 0 } },
            y1: { position: 'right', min: Math.max(0.5, +(pcMn - pcR * 0.1).toFixed(2)), max: Math.min(1.5, +(pcMx + pcR * 0.1).toFixed(2)), ticks: { color: tc(), font: { size: 10 }, stepSize: 0.05 }, grid: { drawOnChartArea: false } },
          },
        },
      }));
    }

    if (decRef.current && dec?.length) {
      const v = [...dec.map(x => x.pe_decay), ...dec.map(x => x.ce_decay)];
      const am = Math.max(Math.abs(Math.min(...v, 0)), Math.abs(Math.max(...v, 0)));
      const sm = Math.ceil(am / 10) * 10 + 10;
      chartsRef.current.push(new Chart(decRef.current, {
        type: 'bar',
        data: {
          labels: dec.map(x => x.time),
          datasets: [
            { label: 'PE', data: dec.map(x => x.pe_decay), backgroundColor: dec.map(x => x.pe_decay >= 0 ? 'rgba(239,83,80,0.72)' : 'rgba(38,166,154,0.52)'), borderWidth: 0 },
            { label: 'CE', data: dec.map(x => x.ce_decay), backgroundColor: dec.map(x => x.ce_decay >= 0 ? 'rgba(38,166,154,0.72)' : 'rgba(239,83,80,0.52)'), borderWidth: 0 },
          ],
        },
        options: {
          plugins: { legend: { display: true, labels: { color: tc(), font: { size: 10 }, boxWidth: 8 } }, tooltip: ttBase() },
          scales: {
            x: { ticks: { color: tc(), font: { size: 9 }, maxRotation: 45 }, grid: { color: gc(), lineWidth: 1, tickLength: 0 } },
            y: { min: -sm, max: sm, ticks: { color: tc(), font: { size: 10 } }, grid: { color: gc(), lineWidth: 1, tickLength: 0 } },
          },
        },
      }));
    }

    // OHLC + FIB
    const d = oh;
    const cv = ohlcRef.current;
    if (cv && d?.labels?.length) {
      const O = d.o, H = d.h, L = d.l, C = d.c, n = d.labels.length;
      const storageKey = `tmr_fib_settings_${symbol}`;
      let saved = null;
      try { saved = JSON.parse(localStorage.getItem(storageKey)); } catch (_) {}
      const defaultOn = { 0: true, '0.382': true, '0.5': true, '0.618': true, 1: true };
      const initialFib = {};
      if (fib) Object.keys(fib).forEach(k => { initialFib[k] = saved ? !!saved[k] : !!defaultOn[k]; });
      setFibOn(initialFib);

      const getRange = () => {
        const validH = H.filter(v => v !== null), validL = L.filter(v => v !== null);
        if (!validH.length) return { yMin: 0, yMax: 100 };
        const candleMin = Math.min(...validL), candleMax = Math.max(...validH);
        const rg = (candleMax - candleMin) || 1;
        return { yMin: candleMin - rg * 0.08, yMax: candleMax + rg * 0.08 };
      };

      const showLabel = k => ['S1','S2','S3','R1','R2','R3'].includes(k);

      const candlePlugin = {
        id: 'ohlcCandles',
        afterDraw(chart) {
          const { ctx, chartArea: A, scales: { x, y } } = chart;
          if (!A) return;
          ctx.imageSmoothingEnabled = false;
          const gn = getComputedStyle(document.documentElement).getPropertyValue('--candle-green').trim() || '#26a69a';
          const rd = getComputedStyle(document.documentElement).getPropertyValue('--candle-red').trim() || '#ef5350';
          const fibState = chart.$fibOn || {};
          if (fib) Object.keys(fib).forEach(k => {
            if (!fibState[k]) return;
            const py = y.getPixelForValue(fib[k]);
            if (py < A.top - 2 || py > A.bottom + 2) return;
            ctx.save();
            ctx.beginPath(); ctx.strokeStyle = FC[k] || '#666'; ctx.lineWidth = 1;
            ctx.setLineDash([4, 4]); ctx.globalAlpha = 0.35;
            ctx.moveTo(A.left, py); ctx.lineTo(A.right, py); ctx.stroke();
            if (showLabel(k)) {
              ctx.setLineDash([]); ctx.globalAlpha = 0.9;
              ctx.font = '700 10px sans-serif'; ctx.fillStyle = FC[k] || '#aaa';
              ctx.textAlign = 'right'; ctx.textBaseline = 'middle';
              ctx.fillText(k, A.right - 4, py);
            }
            ctx.restore();
          });
          const bw = Math.max(3, Math.min(10, (A.right - A.left) / n * 0.55));
          for (let i = 0; i < n; i++) {
            if (O[i] === null) continue;
            const px = x.getPixelForValue(i);
            const oY = y.getPixelForValue(O[i]), cY = y.getPixelForValue(C[i]);
            const hY = y.getPixelForValue(H[i]), lY = y.getPixelForValue(L[i]);
            const bull = C[i] >= O[i], clr = bull ? gn : rd;
            ctx.beginPath(); ctx.strokeStyle = clr; ctx.lineWidth = 1;
            ctx.moveTo(px, hY); ctx.lineTo(px, lY); ctx.stroke();
            const bt = Math.min(oY, cY), bh = Math.max(Math.abs(cY - oY), 1);
            ctx.fillStyle = clr; ctx.fillRect(px - bw / 2, bt, bw, bh);
          }
        },
      };

      const rebuild = (fibState) => {
        if (ohlcChartRef.current) { ohlcChartRef.current.destroy(); ohlcChartRef.current = null; }
        const r = getRange();
        const chart = new Chart(cv.getContext('2d'), {
          type: 'line',
          data: { labels: d.labels, datasets: [{ data: C.map(() => null) }] },
          options: {
            animation: false,
            plugins: { legend: { display: false }, tooltip: { enabled: false } },
            scales: {
              x: { ticks: { color: tc(), font: { size: 9 }, maxTicksLimit: 10 }, grid: { color: gc(), lineWidth: 1, tickLength: 0 } },
              y: { min: r.yMin, max: r.yMax, position: 'right', ticks: { color: tc(), font: { size: 10 } }, grid: { color: gc(), lineWidth: 1, tickLength: 0 } },
            },
            onHover(event, _els, ch) {
              const info = ohlcInfoRef.current;
              const A = ch.chartArea;
              if (!A || !info) return;
              const rect = cv.getBoundingClientRect();
              const mx = event.native ? event.native.offsetX : (event.x - rect.left);
              if (mx < A.left || mx > A.right) { info.innerHTML = ''; return; }
              const xS = ch.scales.x;
              let ci = -1, md = Infinity;
              for (let i = 0; i < n; i++) {
                if (O[i] === null) continue;
                const dd = Math.abs(mx - xS.getPixelForValue(i));
                if (dd < md) { md = dd; ci = i; }
              }
              if (ci < 0) { info.innerHTML = ''; return; }
              const bl = C[ci] >= O[ci];
              const cl = bl ? 'var(--green)' : 'var(--red)';
              info.innerHTML = `<span>${d.labels[ci]}</span><span style="color:${cl}">O:${O[ci]}</span><span style="color:${cl}">H:${H[ci]}</span><span style="color:${cl}">L:${L[ci]}</span><span style="color:${cl}">C:${C[ci]}</span>`;
            },
          },
          plugins: [candlePlugin],
        });
        chart.$fibOn = fibState;
        ohlcChartRef.current = chart;
        chartsRef.current.push(chart);
      };

      rebuild(initialFib);
      cv.$rebuildOHLC = rebuild;
    }

    return () => {
      chartsRef.current.forEach(c => { try { c.destroy(); } catch (_) {} });
      chartsRef.current = [];
      ohlcChartRef.current = null;
    };
  }, [series, symbol, ohlcDebug]);

  useEffect(() => {
    if (ohlcChartRef.current && ohlcRef.current?.$rebuildOHLC) {
      ohlcChartRef.current.$fibOn = fibOn;
      ohlcChartRef.current.update();
    }
  }, [fibOn]);

  const toggleFib = (k, on) => {
    const next = { ...fibOn, [k]: on };
    setFibOn(next);
    try { localStorage.setItem(`tmr_fib_settings_${symbol}`, JSON.stringify(next)); } catch (_) {}
  };

  const fib = series?.fib || {};

  return (
    <div className="oc-ch" id="ocCharts">
      <div className="oc-ch-h"><i className="bi bi-graph-up" /> OPTION METRICS</div>
      <div className="oc-cg">
        <div className="oc-c"><div className="oc-ct">OCI (TOP 6 OI)</div><div className="oc-cv"><canvas ref={ociRef} /></div></div>
        <div className="oc-c">
          <div className="oc-ct">
            OHLC + FIB ({symbol})
            <span className="fib-ctrl" ref={fibCtrlRef}>
              {Object.keys(fib).length > 0 && (
                <>
                  <label style={{ color: '#5B9CF6', fontWeight: 700 }}>
                    <input type="checkbox" onChange={e => Object.keys(fib).forEach(k => toggleFib(k, e.target.checked))} checked={Object.values(fibOn).every(Boolean)} /> ALL
                  </label>
                  {Object.keys(fib).map(k => (
                    <label key={k} style={{ color: FC[k] || '#888' }}>
                      <input type="checkbox" checked={!!fibOn[k]} onChange={e => toggleFib(k, e.target.checked)} /> {k}
                    </label>
                  ))}
                </>
              )}
            </span>
          </div>
          <div className="oc-cv">
            <div className="ohlc-info" ref={ohlcInfoRef} />
            <canvas ref={ohlcRef} />
            {!series?.oh?.labels?.length && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'var(--text-dim)' }}>
                {ohlcDebug || 'No OHLC data'}
              </div>
            )}
          </div>
        </div>
        <div className="oc-c"><div className="oc-ct">MAXPAIN · SPOT · PCR</div><div className="oc-cv"><canvas ref={mpRef} /></div></div>
        <div className="oc-c"><div className="oc-ct">PREMIUM DECAY (AVG TOP 6)</div><div className="oc-cv"><canvas ref={decRef} /></div></div>
      </div>
    </div>
  );
}